import React, { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LinkContext } from "../../LinkProvider";

import towerImg from "../../img/markets/banner-tower.webp";
import towerMobileImg from "../../img/markets/banner-tower-mobile.webp";
import cloudImg from "../../img/markets/cloud.webp";

/* Плашки рынков поверх картинки — позиции из макета (доли от 1520x713) */
const DESKTOP_CHIPS = [
  { key: "markets.banner.chip.forex", left: "82.9%", top: "12.5%" },
  { key: "markets.banner.chip.stocks", left: "75.8%", top: "34.1%" },
  { key: "markets.banner.chip.indices", left: "58%", top: "41.9%" },
  { key: "markets.banner.chip.crypto", left: "81.9%", top: "61.8%" },
  { key: "markets.banner.chip.commodities", left: "54.1%", top: "77%" },
];

const MOBILE_CHIPS = [
  "markets.banner.chip.stocks",
  "markets.banner.chip.commodities",
  "markets.banner.chip.forex",
  "markets.banner.chip.indices",
  "markets.banner.chip.crypto",
];

const BTN_GRADIENT =
  "linear-gradient(-84deg, rgba(14,28,54,0.8) 6%, rgba(3,65,180,0.8) 66%)";
const BTN_GRADIENT_LIGHT =
  "linear-gradient(-84deg, rgba(14,28,54,0.4) 6%, rgba(3,65,180,0.4) 66%)";

function Banner() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);
  const sectionRef = useRef(null);

  /* Mouse-parallax: сглаженные --px/--py в диапазоне -1..1 (как в hero главной) */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(hover: none)").matches
    )
      return;

    let raf;
    let tx = 0,
      ty = 0,
      cx = 0,
      cy = 0;
    const clamp = (v) => Math.max(-1, Math.min(1, v));

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      tx = -clamp(((e.clientX - r.left) / r.width) * 2 - 1);
      ty = -clamp(((e.clientY - r.top) / r.height) * 2 - 1);
    };

    const tick = () => {
      cx += (tx - cx) * 0.05;
      cy += (ty - cy) * 0.05;
      el.style.setProperty("--px", cx.toFixed(4));
      el.style.setProperty("--py", cy.toFixed(4));
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-white mt-[55px] lg:mt-[70px]"
    >
      {/* ── Desktop ── */}
      <div className="hidden lg:block relative h-[713px]">
        {/* Фон: башня в облаках + осветление слева и снизу */}
        <div className="hero-par absolute inset-0 [--par:10] [--par-y:6] [--par-s:1.03] pointer-events-none select-none">
          <img
            src={towerImg}
            alt=""
            aria-hidden
            className="hero-a-fade absolute inset-0 w-full h-full object-cover object-bottom"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 75%, rgba(255,255,255,0.8) 91%, #ffffff 100%), linear-gradient(90deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.7) 48.5%, rgba(230,230,230,0) 63.7%, rgba(255,255,255,0) 100%)",
            }}
          />
        </div>

        {/* Облака по бокам (ближний слой, дрейфуют) */}
        <div className="hero-par absolute inset-0 [--par:34] [--par-y:16] pointer-events-none select-none">
          <div className="hero-a-fade absolute left-[-9%] bottom-[-8%] w-[42%]" style={{ animationDelay: ".7s" }}>
            <img src={cloudImg} alt="" aria-hidden className="hero-cloud-a w-full h-auto" />
          </div>
          <div className="hero-a-fade absolute left-[28%] bottom-[-14%] w-[46%]" style={{ animationDelay: ".95s" }}>
            <img src={cloudImg} alt="" aria-hidden className="hero-cloud-b w-full h-auto -scale-x-100" />
          </div>
          <div className="hero-a-fade absolute right-[-10%] bottom-[-6%] w-[44%]" style={{ animationDelay: ".8s" }}>
            <img src={cloudImg} alt="" aria-hidden className="hero-cloud-c w-full h-auto" />
          </div>
          <div className="hero-a-fade absolute right-[-6%] top-[34%] w-[34%]" style={{ animationDelay: "1.1s" }}>
            <img src={cloudImg} alt="" aria-hidden className="hero-cloud-b w-full h-auto -scale-x-100" />
          </div>
        </div>

        {/* Плашки рынков — появляются каскадом и парят */}
        <div className="hero-par absolute inset-0 [--par:22] [--par-y:12]">
          {DESKTOP_CHIPS.map((chip, i) => (
            <div
              key={chip.key}
              className="hero-a-fade absolute"
              style={{ left: chip.left, top: chip.top, animationDelay: `${0.6 + i * 0.14}s` }}
            >
              <div
                className="hero-i-float backdrop-blur-[2px] bg-[rgba(255,255,255,0.5)] flex items-center justify-center px-[18px] py-[6px] rounded-[30px]"
                style={{ animationDuration: `${5.5 + i * 0.7}s`, animationDelay: `${1.7 + i * 0.4}s` }}
              >
                <span className="font-['Inter'] font-bold text-[21px] text-[#1c385f] whitespace-nowrap">
                  {t(chip.key)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Текст слева */}
        <div className="hero-par [--par:5] [--par-y:3] relative glcontainer h-full">
          <h1 className="hero-a-up pt-[82px] font-['Inter'] font-black text-[72px] leading-[1.3] text-[#1c385f] max-w-[547px]">
            {t("markets.banner.title1")}
          </h1>
          <p
            className="hero-a-up mt-[4px] font-['Inter'] font-black text-[42px] leading-[1.3] text-[#1c385f] max-w-[507px]"
            style={{ animationDelay: ".12s" }}
          >
            {t("markets.banner.title2")}
          </p>
          <p
            className="hero-a-up mt-[84px] font-['Inter'] font-bold text-[21px] text-[#1c385f] max-w-[434px]"
            style={{ animationDelay: ".24s" }}
          >
            {t("markets.banner.tagline")}
          </p>
          <p className="hero-a-up mt-[16px] font-16-14 max-w-[434px]" style={{ animationDelay: ".32s" }}>
            {t("markets.banner.text1")}
          </p>
          <p className="hero-a-up mt-[12px] font-16-14 max-w-[434px]" style={{ animationDelay: ".4s" }}>
            {t("markets.banner.text2")}
          </p>
          <a
            href={link}
            className="hero-a-up hero-cta mt-[60px] inline-flex items-center justify-center px-[42px] py-[18px] rounded-[40px] border border-white font-['Inter'] font-bold text-[16px] text-white capitalize backdrop-blur-[15px] hover:opacity-90 transition-opacity"
            style={{ animationDelay: ".52s", backgroundImage: BTN_GRADIENT }}
          >
            {t("markets.banner.cta")}
          </a>
        </div>
      </div>

      {/* ── Mobile / tablet ── */}
      <div className="lg:hidden relative">
        <div className="relative overflow-hidden pb-[10px]">
          {/* Фон: башня, прижата к низу блока */}
          <div className="absolute left-0 right-0 top-[40px] bottom-0 pointer-events-none select-none">
            <img
              src={towerMobileImg}
              alt=""
              aria-hidden
              className="hero-a-fade absolute inset-0 mt-[1px] w-full h-[calc(100%+10px)] object-cover object-bottom"
            />
            {/* Верх башни (стеклянные грани) прячем в белой дымке, не зумя картинку */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 72%, rgba(255,255,255,0.85) 90%, #ffffff 100%), linear-gradient(180deg, #ffffff 0%, rgba(255,255,255,0.85) 18%, rgba(255,255,255,0.45) 36%, rgba(255,255,255,0) 56%)",
              }}
            />

            {/* Облака поверх зоны перехода */}
            <div className="hero-a-fade absolute left-[-18%] top-[16%] w-[70%]" style={{ animationDelay: ".6s" }}>
              <img src={cloudImg} alt="" aria-hidden className="hero-cloud-a w-full h-auto" />
            </div>
            <div className="hero-a-fade absolute right-[-20%] top-[22%] w-[75%]" style={{ animationDelay: ".8s" }}>
              <img src={cloudImg} alt="" aria-hidden className="hero-cloud-b w-full h-auto -scale-x-100" />
            </div>
            <div className="hero-a-fade absolute left-[15%] top-[28%] w-[64%]" style={{ animationDelay: "1s" }}>
              <img src={cloudImg} alt="" aria-hidden className="hero-cloud-c w-full h-auto" />
            </div>
          </div>

          {/* Контент поверх */}
          <div className="relative flex flex-col items-center pt-[25px] px-[18px]">
            <h1 className="hero-a-up font-['Inter'] font-black text-[40px] leading-[1.3] text-[#1c385f] text-center">
              {t("markets.banner.title1")}
            </h1>
            <p
              className="hero-a-up font-['Inter'] font-black text-[23px] leading-[1.3] text-[#1c385f] text-center"
              style={{ animationDelay: ".12s" }}
            >
              {t("markets.banner.title2")}
            </p>

            <div className="mt-[17px] max-w-[296px] flex flex-wrap items-center justify-center gap-[15px]">
              {MOBILE_CHIPS.map((key, i) => (
                <div
                  key={key}
                  className="hero-a-fade backdrop-blur-[2px] bg-[rgba(138,167,202,0.4)] flex items-center justify-center px-[15px] py-[4px] rounded-[30px]"
                  style={{ animationDelay: `${0.3 + i * 0.1}s` }}
                >
                  <span className="font-['Inter'] font-bold text-[12px] text-[#1c385f] whitespace-nowrap">
                    {t(key)}
                  </span>
                </div>
              ))}
            </div>

            <a
              href={link}
              className="hero-a-up hero-cta mt-[19px] inline-flex items-center justify-center px-[28px] py-[12px] rounded-[26px] border border-white font-['Inter'] font-bold text-[14px] text-white capitalize backdrop-blur-[10px]"
              style={{ animationDelay: ".4s", backgroundImage: BTN_GRADIENT_LIGHT }}
            >
              {t("markets.banner.cta")}
            </a>

            {/* Распорка под картинку */}
            <div className="w-full h-[420px] sm:h-[520px]" aria-hidden />
          </div>
        </div>

        {/* Текст под картинкой */}
        <div className="glcontainer mt-[10px]">
          <p className="font-['Inter'] font-bold text-[15px] text-[#1c385f]">
            {t("markets.banner.tagline")}
          </p>
          <p className="mt-[14px] font-16-14">{t("markets.banner.text1")}</p>
          <p className="mt-[15px] font-16-14">{t("markets.banner.text2")}</p>
          <a
            href={link}
            className="mt-[35px] inline-flex items-center justify-center px-[32px] py-[15px] rounded-[40px] border border-white font-['Inter'] font-bold text-[14px] text-white capitalize backdrop-blur-[15px]"
            style={{ backgroundImage: BTN_GRADIENT }}
          >
            {t("markets.banner.cta")}
          </a>
        </div>
      </div>
    </section>
  );
}

export default Banner;
